<template>
    <div class="dashboard-container">
        <div v-if="loading" class="loading-container">
            <v-progress-circular 
                :size="50" 
                :width="5" 
                color="primary" 
                indeterminate
            ></v-progress-circular>
            <p class="loading-text">{{$t('dashboardBi.loading')}}</p>
        </div>

        <!-- Dashboard Iframe -->
        <div class="slideshow-container ajuste" v-if="dashboards.length > 0 && !loading && selectedDashboard">
            <iframe
                ref="dashboardIframe"
                class="mySlides ajuste"
                :src="iframeSrc"
                frameborder="0"
                allowFullScreen="true"
                @load="onIframeLoad"
            ></iframe>
        </div>

        <!-- Dashboard Selector - Navigation Drawer -->
        <v-navigation-drawer
            v-model="showSelector"
            app
            temporary
            left
            width="400"
            class="drawer-selector"
        >
            <v-list-item class="px-2 drawer-header">
                <v-list-item-avatar>
                    <v-icon>mdi-view-dashboard</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                    <v-list-item-title class="text-h6">
                        {{$t('dashboardBi.title')}}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                        {{$t('dashboardBi.selectDashboard')}}
                    </v-list-item-subtitle>
                </v-list-item-content>
                <v-btn 
                    v-if="selectedDashboard" 
                    icon 
                    @click="showSelector = false"
                >
                    <v-icon color="white">mdi-close</v-icon>
                </v-btn>
            </v-list-item>

            <v-divider></v-divider>

            <v-list v-if="dashboards.length > 0" class="pt-2">
                <v-list-item
                    v-for="(dashboard, index) in dashboards"
                    :key="dashboard.id"
                    @click="selectDashboard(dashboard)"
                    class="dashboard-item"
                    :class="{ 'selected-item': selectedDashboard && selectedDashboard.id === dashboard.id }"
                    two-line
                >
                    <v-list-item-avatar :color="dashboard.source === 'progress' ? 'teal lighten-1' : 'primary lighten-1'">
                        <v-icon dark>{{ dashboard.source === 'progress' ? 'mdi-progress-check' : 'mdi-chart-box-outline' }}</v-icon>
                    </v-list-item-avatar>
                    
                    <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">
                            {{ dashboard.titulo_dashboard || `Dashboard ${index + 1}` }}
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="dashboard.descricao_dashboard" class="text-wrap">
                            {{ dashboard.descricao_dashboard }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="dashboard.source === 'progress'" class="text-wrap caption">
                            {{ $t('dashboardBi.typeProgress', 'Progresso cliente') }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-else class="text-wrap caption">
                            {{ $t('dashboardBi.typeBi', 'Business Intelligence') }}
                        </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action>
                        <v-icon 
                            v-if="selectedDashboard && selectedDashboard.id === dashboard.id" 
                            color="primary"
                        >
                            mdi-check-circle
                        </v-icon>
                        <v-icon v-else color="grey lighten-1">
                            mdi-chevron-right
                        </v-icon>
                    </v-list-item-action>
                </v-list-item>
            </v-list>

            <v-alert 
                v-else 
                type="info" 
                text 
                class="ma-4"
            >
                {{$t('dashboardBi.noDashboard')}}
            </v-alert>
        </v-navigation-drawer>

        <!-- Floating Action Button -->
        <v-btn
            v-if="!showSelector && dashboards.length > 0 && !loading"
            fab
            fixed
            top
            left
            color="primary"
            class="floating-btn"
            @click="showSelector = true"
            elevation="6"
        >
            <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
        
        <v-alert 
            v-if="progressWarning" 
            type="warning" 
            dense 
            max-width="98%"
            class="error-alert"
        >
            {{ progressWarning }}
        </v-alert>
        
        <v-alert 
            v-if="error" 
            type="error" 
            dense 
            max-width="98%"
            class="error-alert"
        >
            {{ errorMessage }}
        </v-alert>
    </div>
</template>

<script>
import axios from 'axios';
import apiController from '@/utils/apiController';

export default {
    name: 'DashboardsBI',
    data: () => ({
        user: null,
        token: null,
        dashboards: [],
        loading: true,
        error: false,
        progressWarning: null,
        slideIndex: 0,
        selectedDashboard: null,
        showSelector: true,
        iframeSrc: ''
    }),
    computed: {
        errorMessage() {return this.$t('dashboardBi.failedLoad')},
        progressPortalApi() {
            const configured = process.env.VUE_APP_PROGRESS_PORTAL_API;
            if (configured) {
                return configured.replace(/\/$/, '');
            }
            if (typeof window !== 'undefined' && window.location.origin) {
                return window.location.origin + '/progresso-api';
            }
            return '';
        },
        progressPortalUrl() {
            const configured = process.env.VUE_APP_PROGRESS_PORTAL_URL;
            if (configured) {
                return configured.replace(/\/$/, '');
            }
            if (typeof window !== 'undefined' && window.location.origin) {
                return window.location.origin + '/progresso';
            }
            return '';
        },
        progressPortalOrigin() {
            try {
                return new URL(this.progressPortalUrl).origin;
            } catch (e) {
                return '';
            }
        }
    },
    watch: {
        '$i18n.locale'() {
            this.getDashboards();
        }
    },
    created() {
        this.user = JSON.parse(sessionStorage.getItem("user"));
        this.token = sessionStorage.getItem("api");
        this.getDashboards();
    },
    mounted() {
        window.addEventListener('message', this.onEmbedMessage);
    },
    beforeDestroy() {
        window.removeEventListener('message', this.onEmbedMessage);
    },
    methods: {
        normalizeBiDashboard(dashboard, index) {
            return {
                ...dashboard,
                id: dashboard.id_dashboard != null ? `bi-${dashboard.id_dashboard}` : `bi-${index}`,
                source: 'bi'
            };
        },
        normalizeProgressDashboard(dashboard) {
            const slug = dashboard.slug;
            const embedUrl = `${this.progressPortalUrl}/embed/${slug}?embed=true`;
            return {
                id: `progress-${slug}`,
                slug,
                titulo_dashboard: dashboard.titulo_dashboard || dashboard.name,
                descricao_dashboard: dashboard.descricao_dashboard || dashboard.description,
                url: embedUrl,
                source: 'progress'
            };
        },
        async fetchBiDashboards() {
            const response = this.user.is_superadmin === 1
                ? await apiController.request(apiController.endpoints.getallDashboards, { silent: true })
                : await apiController.request(apiController.endpoints.getallDashboardspermitidos, {
                    params: { id: this.user.id_usuario },
                    silent: true,
                });
            return (response.data || []).map((d, i) => this.normalizeBiDashboard(d, i));
        },
        fetchProgressDashboards() {
            if (!this.progressPortalApi) {
                return Promise.resolve([]);
            }
            return axios.get(this.progressPortalApi + '/v1/dashboards', {
                headers: { 'x-access-token': this.token },
                timeout: 10000
            }).then(response => {
                return (response.data || []).map((d) => this.normalizeProgressDashboard(d));
            }).catch((error) => {
                console.log('Progress portal unavailable', error);
                this.progressWarning = this.$t('dashboardBi.progressUnavailable', 'Dashboards de progresso indisponíveis no momento.');
                return [];
            });
        },
        getDashboards() {
            this.loading = true;
            this.error = false;
            this.progressWarning = null;

            Promise.all([
                this.fetchBiDashboards().catch((error) => {
                    console.log(error);
                    return [];
                }),
                this.fetchProgressDashboards()
            ]).then(([biDashboards, progressDashboards]) => {
                this.dashboards = [...biDashboards, ...progressDashboards];

                if (this.dashboards.length === 0) {
                    this.error = true;
                    this.showSelector = false;
                } else {
                    this.showSelector = true;
                }
                this.loading = false;
            }).catch((error) => {
                console.log(error);
                this.error = true;
                this.loading = false;
                this.showSelector = false;
            });
        },
        buildBiIframeSrc(url) {
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}embed=true&toolbar=false&navContentPaneEnabled=false`;
        },
        selectDashboard(dashboard) {
            this.selectedDashboard = dashboard;
            if (dashboard.source === 'progress') {
                this.iframeSrc = dashboard.url;
            } else {
                this.iframeSrc = this.buildBiIframeSrc(dashboard.url);
            }
            this.showSelector = false;
        },
        sendEmbedAuth() {
            if (!this.selectedDashboard || this.selectedDashboard.source !== 'progress') {
                return;
            }
            const iframe = this.$refs.dashboardIframe;
            if (!iframe || !iframe.contentWindow || !this.token) {
                return;
            }
            const targetOrigin = this.progressPortalOrigin || '*';
            iframe.contentWindow.postMessage(
                {
                    type: 'merus-auth',
                    token: this.token,
                    user: this.user
                },
                targetOrigin
            );
        },
        onEmbedMessage(event) {
            if (event.data?.type !== 'progress-portal-ready') {
                return;
            }
            const expectedOrigin = this.progressPortalOrigin;
            if (expectedOrigin && event.origin !== expectedOrigin) {
                return;
            }
            this.sendEmbedAuth();
        },
        onIframeLoad() {
            this.sendEmbedAuth();
        }
        // showSlides() {    
        //     var i;    
        //     var slides = document.getElementsByClassName("mySlides"); 

        //     for (i = 0; i < slides.length; i++) {
        //         slides[i].style.display = "none";    
        //     }    

        //     if (this.slideIndex >= slides.length) {
        //         this.slideIndex = 0;
        //     }    

        //     slides[this.slideIndex].style.display = "block";

        //     setTimeout(this.showSlides, 5000);
        //     this.slideIndex++; 
        // }
    }
}
</script>

<style scoped>
.dashboard-container {
    width: 100%;
    height: 90vh;
    position: relative;
    overflow: hidden;
    background-color: transparent;
    margin-top: 2%;
}

.loading-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    background-color: #f5f5f5;
}

.loading-text {
    margin-top: 16px;
    color: #666;
    font-size: 16px;
}

.error-alert {
    margin: 20px auto;
    border-radius: 10px;
    max-width: 600px;
}

/* Remove qualquer padding/margin do container pai se necessário */
.dashboard-container * {
    box-sizing: border-box;
}

iframe {
    vertical-align: center;
    width: 100%;
    height: 100vh;
}
.slideshow-container {  
    max-width: 1000%;
    width: 100%;
    height: 100%;
    position: relative;
    margin: auto;
    margin-top: -12px;
}
.text {  
    color: #f2f2f2;
    font-size: 15px;
    padding: 8px 12px;
    position: absolute;
    bottom: 8px;
    height: 100%; 
    width: 100%;
    text-align: center;
}
.numbertext {
    color: #f2f2f2; 
    font-size: 12px; 
    padding: 8px 12px; 
    position: absolute; 
    top: 0;
}
.dot { 
    height: 15px; 
    width: 15px; 
    margin: 0 2px; 
    background-color: #bbb; 
    border-radius: 50%; 
    display: inline-block; 
    transition: background-color 0.6s ease;
}
.active {  
    background-color: #717171; 
    height: 100%;
}


.fade { 
    -webkit-animation-name: fade; 
    -webkit-animation-duration: 1s; 
    animation-name: fade; 
    animation-duration: 1s;
}
@-webkit-keyframes fade {  
    from {
        opacity: .4;
    } 
    to {
        opacity: 1;
    }
}
@keyframes fade { 
    from {
        opacity: .4;
    } 
    to {
        opacity: 1;
    }
}


.scale-up-center {
    -webkit-animation: scale-up-center 1s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
            animation: scale-up-center 1s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
}
@-webkit-keyframes scale-up-center {
    0% {
        -webkit-transform: scale(0.5);
                transform: scale(0.5);
    }
    100% {
        -webkit-transform: scale(1);
                transform: scale(1);
    }
}
@keyframes scale-up-center {
    0% {
        -webkit-transform: scale(0.5);
                transform: scale(0.5);
    }
    100% {
        -webkit-transform: scale(1);
                transform: scale(1);
    }
}


.scale-down-center {
    -webkit-animation: scale-down-center 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: scale-down-center 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@-webkit-keyframes scale-down-center {
    0% {
        -webkit-transform: scale(2);
                transform: scale(2);
    }
    100% {
        -webkit-transform: scale(1);
            transform: scale(1);
    }
}
@keyframes scale-down-center {
    0% {
        -webkit-transform: scale(1.8);
                transform: scale(1.8);
    }
    100% {
        -webkit-transform: scale(1);
                transform: scale(1);
    }
}


.rotate-center-right {
    -webkit-animation: rotate-center-right 1s ease-in-out both;
            animation: rotate-center-right 1s ease-in-out both;
}
@-webkit-keyframes rotate-center-right {
    0% {
        -webkit-transform: rotate(180deg);
                transform: rotate(180deg);
    }
    100% {
        -webkit-transform: rotate(360deg);
                transform: rotate(360deg);
    }
}
@keyframes rotate-center-right {
    0% {
        -webkit-transform: rotate(180deg);
                transform: rotate(180deg);
    }
    100% {
        -webkit-transform: rotate(360deg);
                transform: rotate(360deg);
    }
}


.rotate-center-left {
    -webkit-animation: rotate-center-left 1s ease-in-out both;
            animation: rotate-center-left 1s ease-in-out both;
}
@-webkit-keyframes rotate-center-left {
    0% {
        -webkit-transform: rotate(-180deg);
                transform: rotate(-180deg);
    }
    100% {
        -webkit-transform: rotate(-360deg);
                transform: rotate(-360deg);
    }
}
@keyframes rotate-center-left {
    0% {
        -webkit-transform: rotate(-180deg);
                transform: rotate(-180deg);
    }
    100% {
        -webkit-transform: rotate(-360deg);
                transform: rotate(-360deg);
    }
}


.rotate-scale-up {
    -webkit-animation: rotate-scale-up 1s linear both;
            animation: rotate-scale-up 1s linear both;
}
@-webkit-keyframes rotate-scale-up {
    0% {
        -webkit-transform: scale(1) rotateZ(180deg);
                transform: scale(1) rotateZ(180deg);
    }
    50% {
        -webkit-transform: scale(1.5) rotateZ(270deg);
                transform: scale(1.5) rotateZ(270deg);
    }
    100% {
        -webkit-transform: scale(1) rotateZ(360deg);
                transform: scale(1) rotateZ(360deg);
    }
}
@keyframes rotate-scale-up {
    0% {
        -webkit-transform: scale(1) rotateZ(180deg);
                transform: scale(1) rotateZ(180deg);
    }
    50% {
        -webkit-transform: scale(1.5) rotateZ(270deg);
                transform: scale(1.5) rotateZ(270deg);
    }
    100% {
        -webkit-transform: scale(1) rotateZ(360deg);
                transform: scale(1) rotateZ(360deg);
    }
}


.rotate-scale-down {
    -webkit-animation: rotate-scale-down 1s linear both;
            animation: rotate-scale-down 1s linear both;
}
@-webkit-keyframes rotate-scale-down {
    0% {
        -webkit-transform: scale(1) rotateZ(180deg);
                transform: scale(1) rotateZ(180deg);
    }
    50% {
        -webkit-transform: scale(0.5) rotateZ(270deg);
                transform: scale(0.5) rotateZ(270deg);
    }
    100% {
        -webkit-transform: scale(1) rotateZ(360deg);
                transform: scale(1) rotateZ(360deg);
    }
}
@keyframes rotate-scale-down {
    0% {
        -webkit-transform: scale(1) rotateZ(180deg);
                transform: scale(1) rotateZ(180deg);
    }
    50% {
        -webkit-transform: scale(0.5) rotateZ(270deg);
                transform: scale(0.5) rotateZ(270deg);
    }
    100% {
        -webkit-transform: scale(1) rotateZ(360deg);
                transform: scale(1) rotateZ(360deg);
    }
}


.flip-bottom {
    -webkit-animation: flip-bottom 1s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
            animation: flip-bottom 1s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
}
@-webkit-keyframes flip-bottom {
    0% {
        -webkit-transform: rotateX(-180deg);
                transform: rotateX(-180deg);
    }
    100% {
        -webkit-transform: rotateX(0deg);
                transform: rotateX(0deg);
    }
}
@keyframes flip-bottom {
    0% {
        -webkit-transform: rotateX(-180deg);
                transform: rotateX(-180deg);
    }
    100% {
        -webkit-transform: rotateX(0deg);
                transform: rotateX(0deg);
    }
}


.flip-top {
    -webkit-animation: flip-top 1s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
            animation: flip-top 1s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
}
@-webkit-keyframes flip-top {
    0% {
        -webkit-transform: rotateX(180deg);
                transform: rotateX(180deg);
    }
    100% {
        -webkit-transform: rotateX(0);
                transform: rotateX(0);
    }
}
@keyframes flip-top {
    0% {
        -webkit-transform: rotateX(180deg);
                transform: rotateX(180deg);
    }
    100% {
        -webkit-transform: rotateX(0);
                transform: rotateX(0);
    }
}


.flip-right {
    -webkit-animation: flip-right 1s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
            animation: flip-right 1s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
}
@-webkit-keyframes flip-right {
    0% {
        -webkit-transform: rotateY(180deg);
                transform: rotateY(180deg);
    }
    100% {
        -webkit-transform: rotateY(0);
                transform: rotateY(0);
    }
}
@keyframes flip-right {
    0% {
        -webkit-transform: rotateY(180deg);
                transform: rotateY(180deg);
    }
    100% {
        -webkit-transform: rotateY(0);
                transform: rotateY(0);
    }
}


.flip-left {
    -webkit-animation: flip-left 1s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
            animation: flip-left 1s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
}
@-webkit-keyframes flip-left {
    0% {
        -webkit-transform: rotateY(-180deg);
                transform: rotateY(-180deg);
    }
    100% {
        -webkit-transform: rotateY(0);
                transform: rotateY(0);
    }
}
@keyframes flip-left {
    0% {
        -webkit-transform: rotateY(-180deg);
                transform: rotateY(-180deg);
    }
    100% {
        -webkit-transform: rotateY(0);
                transform: rotateY(0);
    }
}


.slide-top {
    -webkit-animation: slide-top 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: slide-top 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@-webkit-keyframes slide-top {
    0% {
        -webkit-transform: translateY(400px);
                transform: translateY(400px);
    }
    100% {
        -webkit-transform: translateY(0);
                transform: translateY(0);
    }
}
@keyframes slide-top {
    0% {
        -webkit-transform: translateY(400px);
                transform: translateY(400px);
    }
    100% {
        -webkit-transform: translateY(0);
                transform: translateY(0);
    }
}


.slide-bottom {
    -webkit-animation: slide-bottom 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: slide-bottom 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@-webkit-keyframes slide-bottom {
    0% {
        -webkit-transform: translateY(-400px);
                transform: translateY(-400px);
    }
    100% {
        -webkit-transform: translateY(0);
                transform: translateY(0);
    }
}
@keyframes slide-bottom {
    0% {
        -webkit-transform: translateY(-400px);
                transform: translateY(-400px);
    }
    100% {
        -webkit-transform: translateY(0);
                transform: translateY(0);
    }
}


.slide-right {
    -webkit-animation: slide-right 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
        animation: slide-right 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@-webkit-keyframes slide-right {
    0% {
        -webkit-transform: translateX(-400px);
                transform: translateX(-400px);
    }
    100% {
        -webkit-transform: translateX(0);
                transform: translateX(0);
    }
}
@keyframes slide-right {
    0% {
        -webkit-transform: translateX(-400px);
                transform: translateX(-400px);
    }
    100% {
        -webkit-transform: translateX(0);
                transform: translateX(0);
    }
}


.slide-left {
    -webkit-animation: slide-left 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
        animation: slide-left 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@-webkit-keyframes slide-left {
    0% {
        -webkit-transform: translateX(400px);
                transform: translateX(400px);
    }
    100% {
        -webkit-transform: translateX(0);
                transform: translateX(0);
    }
}
@keyframes slide-left {
    0% {
        -webkit-transform: translateX(400px);
                transform: translateX(400px);
    }
    100% {
        -webkit-transform: translateX(0);
                transform: translateX(0);
    }
}
.scale-up-hor-center {
    -webkit-animation: scale-up-hor-center 1s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
        animation: scale-up-hor-center 1s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
}
@-webkit-keyframes scale-up-hor-center {
    0% {
    -webkit-transform: scaleX(0.4);
            transform: scaleX(0.4);
    }
    100% {
        -webkit-transform: scaleX(1);
                transform: scaleX(1);
    }
}
@keyframes scale-up-hor-center {
    0% {
        -webkit-transform: scaleX(0.4);
                transform: scaleX(0.4);
    }
    100% {
        -webkit-transform: scaleX(1);
                transform: scaleX(1);
    }
}

.scale-down-hor-center {
    -webkit-animation: scale-down-hor-center 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: scale-down-hor-center 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@-webkit-keyframes scale-down-hor-center {
    0% {
        -webkit-transform: scaleX(1.4);
                transform: scaleX(1.4);
    }
    100% {
        -webkit-transform: scaleX(1);
                transform: scaleX(1);
    }
}
@keyframes scale-down-hor-center {
    0% {
        -webkit-transform: scaleX(1.4);
                transform: scaleX(1.4);
    }
    100% {
        -webkit-transform: scaleX(1);
                transform: scaleX(1);
    }
}

.flip-scale-up-ver {
    -webkit-animation: flip-scale-up-ver 1s linear both;
            animation: flip-scale-up-ver 1s linear both;
}
@-webkit-keyframes flip-scale-up-ver {
    0% {
        -webkit-transform: scale(1) rotateY(180deg);
                transform: scale(1) rotateY(180deg);
    }
    50% {
        -webkit-transform: scale(2.5) rotateY(90deg);
                transform: scale(2.5) rotateY(90deg);
    }
    100% {
        -webkit-transform: scale(1) rotateY(0);
                transform: scale(1) rotateY(0);
    }
}
@keyframes flip-scale-up-ver {
    0% {
        -webkit-transform: scale(1) rotateY(180deg);
                transform: scale(1) rotateY(180deg);
    }
    50% {
        -webkit-transform: scale(2.5) rotateY(90deg);
                transform: scale(2.5) rotateY(90deg);
    }
    100% {
    -webkit-transform: scale(1) rotateY(0);
            transform: scale(1) rotateY(0);
    }
}

.flip-scale-up-hor {
    -webkit-animation: flip-scale-up-hor 1s linear both;
            animation: flip-scale-up-hor 1s linear both;
}
@-webkit-keyframes flip-scale-up-hor {
    0% {
        -webkit-transform: scale(1) rotateX(-180deg);
                transform: scale(1) rotateX(-180deg);
    }
    50% {
        -webkit-transform: scale(2.5) rotateX(-90deg);
                transform: scale(2.5) rotateX(-90deg);
    }
    100% {
        -webkit-transform: scale(1) rotateX(0);
                transform: scale(1) rotateX(0);
    }
}
@keyframes flip-scale-up-hor {
    0% {
        -webkit-transform: scale(1) rotateX(-180deg);
                transform: scale(1) rotateX(-180deg);
    }
    50% {
        -webkit-transform: scale(2.5) rotateX(-90deg);
                transform: scale(2.5) rotateX(-90deg);
    }
    100% {
        -webkit-transform: scale(1) rotateX(0);
                transform: scale(1) rotateX(0);
    }
}
.rotate-90-left {
    -webkit-animation: rotate-90-left 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: rotate-90-left 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@-webkit-keyframes rotate-90-left {
    0% {
        -webkit-transform: rotate(90deg);
                transform: rotate(90deg);
        -webkit-transform-origin: bottom;
                transform-origin: bottom;
    }
    100% {
        -webkit-transform: rotate(0);
                transform: rotate(0);
        -webkit-transform-origin: bottom;
                transform-origin: bottom;
    }
}
@keyframes rotate-90-left {
    0% {
        -webkit-transform: rotate(90deg);
                transform: rotate(90deg);
        -webkit-transform-origin: bottom;
                transform-origin: bottom;
    }
    100% {
        -webkit-transform: rotate(0);
                transform: rotate(0);
        -webkit-transform-origin: bottom;
                transform-origin: bottom;
    }
}
.rotate-90-right {
    -webkit-animation: rotate-90-right 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: rotate-90-right 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@-webkit-keyframes rotate-90-right {
    0% {
        -webkit-transform: rotate(-90deg);
                transform: rotate(-90deg);
        -webkit-transform-origin: bottom;
                transform-origin: bottom;
    }
    100% {
        -webkit-transform: rotate(0);
                transform: rotate(0);
        -webkit-transform-origin: bottom;
                transform-origin: bottom;
    }
}
@keyframes rotate-90-right {
    0% {
        -webkit-transform: rotate(-90deg);
                transform: rotate(-90deg);
        -webkit-transform-origin: bottom;
                transform-origin: bottom;
    }
    100% {
        -webkit-transform: rotate(0);
                transform: rotate(0);
        -webkit-transform-origin: bottom;
                transform-origin: bottom;
    }
}
.roll-in-left {
    -webkit-animation: roll-in-left 1s ease-out both;
            animation: roll-in-left 1s ease-out both;
}
@-webkit-keyframes roll-in-left {
    0% {
        -webkit-transform: translateX(-800px) rotate(-540deg);
                transform: translateX(-800px) rotate(-540deg);
        opacity: 0;
    }
    100% {
        -webkit-transform: translateX(0) rotate(0deg);
                transform: translateX(0) rotate(0deg);
        opacity: 1;
    }
}
@keyframes roll-in-left {
    0% {
        -webkit-transform: translateX(-800px) rotate(-540deg);
                transform: translateX(-800px) rotate(-540deg);
        opacity: 0;
    }
    100% {
        -webkit-transform: translateX(0) rotate(0deg);
                transform: translateX(0) rotate(0deg);
        opacity: 1;
    }
}
.roll-in-right {
    -webkit-animation: roll-in-right 1s ease-out both;
            animation: roll-in-right 1s ease-out both;
}
@-webkit-keyframes roll-in-right {
    0% {
        -webkit-transform: translateX(800px) rotate(540deg);
                transform: translateX(800px) rotate(540deg);
        opacity: 0;
    }
    100% {
        -webkit-transform: translateX(0) rotate(0deg);
                transform: translateX(0) rotate(0deg);
        opacity: 1;
    }
}
@keyframes roll-in-right {
    0% {
        -webkit-transform: translateX(800px) rotate(540deg);
                transform: translateX(800px) rotate(540deg);
        opacity: 0;
    }
    100% {
        -webkit-transform: translateX(0) rotate(0deg);
                transform: translateX(0) rotate(0deg);
        opacity: 1;
    }
}
.jello {
    -webkit-animation: jello 1s both;
            animation: jello 1s both;
}
@-webkit-keyframes jello {
    0% {
        -webkit-transform: scale3d(1, 1, 1);
                transform: scale3d(1, 1, 1);
    }
    30% {
        -webkit-transform: scale3d(1.25, 0.75, 1);
                transform: scale3d(1.25, 0.75, 1);
    }
    40% {
        -webkit-transform: scale3d(0.75, 1.25, 1);
                transform: scale3d(0.75, 1.25, 1);
    }
    50% {
        -webkit-transform: scale3d(1.15, 0.85, 1);
                transform: scale3d(1.15, 0.85, 1);
    }
    65% {
        -webkit-transform: scale3d(0.95, 1.05, 1);
                transform: scale3d(0.95, 1.05, 1);
    }
    75% {
        -webkit-transform: scale3d(1.05, 0.95, 1);
                transform: scale3d(1.05, 0.95, 1);
    }
    100% {
        -webkit-transform: scale3d(1, 1, 1);
                transform: scale3d(1, 1, 1);
    }
}
@keyframes jello {
    0% {
        -webkit-transform: scale3d(1, 1, 1);
                transform: scale3d(1, 1, 1);
    }
    30% {
        -webkit-transform: scale3d(1.25, 0.75, 1);
                transform: scale3d(1.25, 0.75, 1);
    }
    40% {
        -webkit-transform: scale3d(0.75, 1.25, 1);
                transform: scale3d(0.75, 1.25, 1);
    }
    50% {
        -webkit-transform: scale3d(1.15, 0.85, 1);
                transform: scale3d(1.15, 0.85, 1);
    }
    65% {
        -webkit-transform: scale3d(0.95, 1.05, 1);
                transform: scale3d(0.95, 1.05, 1);
    }
    75% {
        -webkit-transform: scale3d(1.05, 0.95, 1);
                transform: scale3d(1.05, 0.95, 1);
    }
    100% {
        -webkit-transform: scale3d(1, 1, 1);
                transform: scale3d(1, 1, 1);
    }
}


@media only screen and (max-width: 100%) {
    .text {
        font-size: 11px;
    }
}
.ajuste{ 
    margin-left: 5%;
    width: 90% !important;
    height: 100vh !important;
}
.none{
    display: none;
}

/* Dashboard Selector Styles */
.drawer-selector {
    z-index: 1500 !important;
}

.drawer-header {
    padding: 16px !important;
    min-height: 80px;
}

.dashboard-item {
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 8px;
    margin: 4px 8px;
    padding: 12px 8px;
}

.dashboard-item:hover {
    background-color: #f5f5f5;
    transform: translateX(4px);
}

.selected-item {
    background-color: #e3f2fd !important;
    border-right: 4px solid #1976d2;
}

.selected-item:hover {
    background-color: #bbdefb !important;
}

.dashboard-item .v-list-item__subtitle {
    white-space: normal !important;
    line-height: 1.4;
    margin-top: 4px;
}

.floating-btn {
    margin: 80px 0 0 20px;
    z-index: 1000;
}

.floating-btn:hover {
    transform: scale(1.1);
    transition: transform 0.2s ease;
}
</style>